<template>
  <div class="search-layout">
    <transition name="fade">
      <UserPage
        v-show="profileModalVisible"
        @closeUser="profileModalVisible = false"
        class="modal"/>
    </transition>

    <transition name="fade">
      <FacetList
        v-show="facetListVisible"
        @close="facetListVisible = false"
        @applyFilter="applyFilter"
        :fieldName="facetListField"
        :selectedFilters="getSelectedFilters"
        class="modal"/>
    </transition>

    <Header 
      @updateSearch="executeSearch(true)"
      @showCart="VisiblePanelComponent === 'CartComponent' ? 
        VisiblePanelComponent = 'ResultsGridComponent' : 
        VisiblePanelComponent = 'CartComponent'" 
      @showUser="profileModalVisible = true" />

    <div class="search-toolbar">
      <button @click="filterPanelVisible = !filterPanelVisible"
        class="filter-toggle"
        v-if="!shouldHideFilters">Filters</button>
      <VoyagerSplitButton :items="mapDropdownOptions"
        class="map-options-toggle" />
    </div>

    <div class="search-area">
      <Transition name="slide" >
        <div class="filter-panel" v-show="filterPanelVisible">
          <OverlayScrollbars 
            class="filters-scroller" 
            ref="filtersScrollbar"
            :options="filterScrollOptions">
            <FilterPanel 
              @user-updated-filter="handleFilterChange"
              @showAllFacets="showAllFacets"/>
          </OverlayScrollbars>
        </div>
      </Transition>
    
      <Splitpanes class="default-theme"
        @resized="updateStoredMapSize($event)">
        <pane :size="mapSize">
          <div class="map-container">
            <VoyagerMap ref="searchMap"
              :baseLayers="baseLayers"
              :results="mapResults"
              :context="mapContext"
              :cartListIds="getAllCartItemIDs().value"
              @userAction="handleUserAction"
              class="map" />
          </div>
        </pane>

        <pane class="main-pane" 
          :size="mainPaneSize">
          <span v-if="loadingState === 'error'">
            Unable to load results, please modify filters or contact system administrator.
          </span>

          <KeepAlive>
              <Suspense>
                <Component 
                  :is="ComponentMap[VisiblePanelComponent]"
                  :sort="sort"
                  :loading="loadingState === 'loading'"
                  :results="(results as any)"
                  @loadNextPage="loadNextPage"
                  @toggleSortDir="handleToggleSortDir"
                  @setSortKey="setSortKey"
                  @userAction="handleUserAction"

                  :result="detailsResult"
                  @closeDetails="hideDetails"

                  @closeCart="() => { VisiblePanelComponent = 'ResultsGridComponent' }"
                />
            </Suspense>
          </KeepAlive>
        </pane>
      </Splitpanes>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, Ref, onMounted, computed, markRaw, Raw, defineAsyncComponent, ModelRef } from 'vue';
import { router } from '@/router';
import { convertLegacyDocs, convertLegacyFilter, getLegacyFilterDefinitions, getLegacyFiltersFromUrlQuery, getODNFilters } from '@/services/LegacyConversionService';
import { QueryDocs, fetchDocument, updateFacetBucketsFromSolr } from '@/services/SolrService';
import { useRoute } from 'vue-router'
import { useRouteQuery } from '@vueuse/router'

import Header from '@/components/SearchHeader.vue';
import FilterPanel from '@/components/FilterPanel.vue';
import UserPage from '@/components/UserPage.vue';
import FacetList from '@/components/FacetList.vue';

import { SearchBar, VoyagerMap, VoyagerSplitButton, VoyagerResult, AnyFilterDefinition } from 'VoyagerComponents';
import { MapIcon, MapOffIcon } from 'vue-tabler-icons';
import { Splitpanes, Pane } from 'splitpanes';
import 'splitpanes/dist/splitpanes.css';

import { useResultsStore } from '@/store/results';
import { useCartStore } from '@/store/cart';
import { useQueryStore } from '@/store/query';
import { off } from 'process';

const DetailsComponent = defineAsyncComponent(() => import('@/components/DetailsTable.vue'))
const CartComponent = defineAsyncComponent(() => import('@/components/CartTool.vue'))
const ResultsGridComponent = defineAsyncComponent(() => import('@/components/ResultsGrid.vue'))

const profileModalVisible = defineModel('profileModalVisible', {default: false, required: false}),
  filterPanelVisible = defineModel('filterPanelVisible', {default: true, required: false}),
  mapVisible = defineModel('mapVisible',{default: true, required: false}),
  mapSize = defineModel('mapSize',{default: 45, required: false}),
  mapContext = defineModel('mapContext', {default: 'search' as 'search' | 'cart' | 'details'}),
  detailsResult = defineModel('detailsResult', {default: undefined, required: false}) as ModelRef<VoyagerResult | undefined>;

const VisiblePanelComponent: Ref<'ResultsGridComponent' | 'CartComponent' | 'DetailsComponent'> = ref('ResultsGridComponent');
const ComponentMap = {
  DetailsComponent,
  CartComponent,
  ResultsGridComponent
}

const baseLayers: Ref<(L.Layer & {name: string})[]> = ref([]);

const {
      setFilter,
      getFilter,
      getQueryState,
      removeFilter,
      toggleSelectFilterValue,
      toggleSortDir, 
      setSortField, 
      incrementPage,
      setFilterValuesFromUrlQuery,
      setSortFromUrlQuery,
      setPlaceFromUrlQuery
    } = useQueryStore(),
  { setTotalFound: setTotalFound, getResultsList, removeAllResults, setResult } = useResultsStore(),
  { addItemToCart, removeItemFromCart, getAllCartItemIDs, getReadOnlyCart } = useCartStore(),
  results= computed(() => getResultsList()),
  mapResults = computed(() => {
    if(
        VisiblePanelComponent.value ==='DetailsComponent' && 
        detailsResult.value
      ) return [detailsResult.value];
    if(VisiblePanelComponent.value === 'CartComponent') return Array.from(getReadOnlyCart().value.values());
    return results.value;
  }),
  sort = computed(() => {
    let sort: {dir: string, field: string, available?: {display: string, field: string}[]} = getQueryState().sort || {dir: 'desc', field: 'score'};
    sort.available = [
      {display: 'Relevance', field: 'score'},
      {display: 'Name', field: 'name'},
      {display: 'Publish Date', field: 'fd_publish_date'}
    ]
    return sort;
  }),
  loadingState: Ref<'loading' | 'done' | 'error'> = ref('loading'),
  filterScrollOptions = {
    scrollbars: {
      theme: 'os-theme-light',
    },
  },
  updateStoredMapSize = ($event: {size: number}) => {
    sessionStorage.setItem('mapSize', $event[0].size);
    mapSize.value = $event[0].size;
    if($event[0].size < 1) {
      mapDropdownOptions.value[0].icon = markRaw(MapOffIcon) as any;
      setMapSize(0);
    } else mapDropdownOptions.value[0].icon = markRaw(MapIcon) as any;
  },
  cartResults = computed(() => Array.from(getReadOnlyCart().value.values())),
  searchMap: Ref<any> = ref(null),
  searchBar: Ref<typeof SearchBar | null> = ref(null),
  mainPaneSize = computed(() => 100 - mapSize.value),
  loadNextPage = () => {
    loadingState.value = 'loading';
    incrementPage();
    executeSearch(false);
  },
  showDetails = (result: VoyagerResult) => {
    detailsResult.value = result;
    VisiblePanelComponent.value = 'DetailsComponent';
    router.push('/details/' + detailsResult.value?.id || '')
  },
  hideDetails = () => {
    VisiblePanelComponent.value = 'ResultsGridComponent';
    // router.push('/search')
    router.back();
  },
  setSortKey = (field) => {
    setSortField(field);
    executeSearch();
  },
  handleToggleSortDir = () => {
    toggleSortDir();
    executeSearch();
  },
  setMapSize = (size: number) => {
    if(size > 1) {
      sessionStorage.setItem('showMap', 'true');
      sessionStorage.setItem('mapSize', size.toString());
      mapSize.value = size;
      mapVisible.value = true;
    }
    else {
      sessionStorage.setItem('showMap', 'false');
      mapSize.value = 0;
      mapVisible.value = false;
    }
  }
const mapDropdownOptions: Ref<{
  label: string, icon?: any | Raw<any>, command?: () => void | undefined
}[]> = ref([
  { label: '', icon: markRaw(MapIcon), command: () => {
    if(mapVisible.value) {
      setMapSize(0);
      mapDropdownOptions.value[0].icon = markRaw(MapOffIcon) as any;
    } else { 
      const storedMapSize = sessionStorage.getItem('mapSize') ||' 45';
      setMapSize(parseInt(storedMapSize));
      mapDropdownOptions.value[0].icon = markRaw(MapIcon) as any;
    }
  } },
  { label: 'Small Map', command: () => setMapSize(25)},
  { label: 'Medium Map', command: () => setMapSize(45)},
  { label: 'Large Map', command: () => setMapSize(75)}
])

const shouldHideFilters = computed(() => {
  return VisiblePanelComponent.value === 'DetailsComponent' || VisiblePanelComponent.value === 'CartComponent';
})

const executeSearch = async(clearResults = true) => {
  try {
    loadingState.value = 'loading';
    if(clearResults) { removeAllResults() }  
    const solrResponse = await QueryDocs();
    await updateFacetBucketsFromSolr(solrResponse);
    setTotalFound(solrResponse.response.numFound);
    let extent = solrResponse["extent.bbox"]?.split(' ');
    if(extent && searchMap.value) {
      const coords = [[extent[1], extent[0]], [extent[3], extent[2]]];
      try { searchMap.value.voyagerMap.fitBounds(coords) } 
      catch (error) { console.warn('Couldn\'t set map extent to search results.', error) }
    }
    const results = convertLegacyDocs(solrResponse);
    results.forEach(result => setResult(result));
    loadingState.value = 'done';
  } catch (error) {
    console.error(error);
    loadingState.value = 'error';
  }
}

const applyFilter = (payload: {fieldName: string, value: string}) => {
  let filterDefinition = getFilter(payload.fieldName);
  if(filterDefinition && (filterDefinition.type === 'multi' || filterDefinition.type === 'single')) {
    const option = filterDefinition.availableValues.find(v => v.value === payload.value);
    if(!option) return;
    option.selected = !option.selected;
  }
  executeSearch();
}

const handleFilterChange = (filter) => {
  setFilter(filter);
  executeSearch();
}

function handleUserAction($event) {
  switch ($event.action) {
    case 'view':
      showDetails($event.result);
      break;
    case 'addToList':
      addItemToCart($event.result);
      break;
    case 'removeFromList':
      removeItemFromCart($event.result.id);
      break;
    // toggle and remove are used by filter chips
    case 'toggleSelectFilterValue':
      toggleSelectFilterValue({
        filterName: $event.filterName,
        filterValue: $event.filterValue
      });
      executeSearch();
      break;
    case 'removeFilter':
      removeFilter($event.filterName);
      executeSearch();
      break;
    default:
      break;
  }
}

// params are for things like /details/:initialDetailsID
// query is for query params like ?q=example
const { params, query } = useRoute(); 

const setFilterPanel = async() => {
  try {
    const legacyFilters = [...await getODNFilters(), ...getLegacyFiltersFromUrlQuery(query)];
    console.log('query', query);
    const legacyFilterDefinitions = await getLegacyFilterDefinitions(legacyFilters);
    let filters = legacyFilterDefinitions.map(convertLegacyFilter);
    filters.forEach(filter => setFilter(filter));
    setFilterValuesFromUrlQuery(query);
    setSortFromUrlQuery(query);
    setPlaceFromUrlQuery(query);
    await executeSearch(false);
  } catch (error) {
    console.error('Error: ', error);
    loadingState.value = 'error';
  }
}

const facetListVisible = ref(false);
const facetListField: Ref<string | undefined> = ref(undefined);
const getSelectedFilters = computed(() => {
  if(!facetListField.value) return [];
  const filter = getFilter(facetListField.value);
  if(filter.type !== 'single' && filter.type !== 'multi') return [];
  return filter.availableValues.filter(v => v.selected).map(v => v.value);
});

const showAllFacets = (fieldName) => {
  facetListField.value = fieldName;
  facetListVisible.value = true;
};

onMounted(async() => {
  if(searchMap.value) {
    const defaultBaseLayer: L.Layer & {name: string} = new searchMap.value.L.TileLayer.WMS('https://maps5.geosolutionsgroup.com/geoserver/osm/wms?SERVICE=WMS&VERSION=1.1.1', {
        attribution: 'Rendering <a href="https://www.geosolutionsgroup.com/">GeoSolutions</a> | &copy; Data <a href="http://osm.org">OpenStreetMap</a> contributors, <a href="http://www.openstreetmap.org/copyright">ODbL</a>',
        layers: 'osm',
        format: 'image/png',
        id: 'baselayer'
    });
    defaultBaseLayer.name = 'Open Street Maps';
    baseLayers.value.push(defaultBaseLayer);
  };
  const storedMapSize = sessionStorage.getItem('mapSize');
  if(storedMapSize) mapSize.value = parseInt(storedMapSize);
  const storedShowMap = sessionStorage.getItem('showMap');
  if(storedShowMap !== undefined && storedShowMap !== null) mapVisible.value = storedShowMap.toLowerCase() === 'true';

  setSortField('fd_publish_date');
  await setFilterPanel();
  let detailsId = params.initialDetailsID;
  if(params.initialDetailsID) {
    if(Array.isArray(detailsId)) detailsId = detailsId[0];
    await fetchDocument(detailsId).then((res) => {
      detailsResult.value = res;
      VisiblePanelComponent.value = 'DetailsComponent';
    }).catch(err => {
      console.log('unable to load details for document.', err);
      VisiblePanelComponent.value = 'ResultsGridComponent';
    });
  }
})

</script>

<style lang="scss">
.search-layout {
  color: var(--color-text-primary);
  display: flex;
  flex-direction: column;
  grid-template-columns: 1fr 1fr 1fr 1fr 1fr 1fr;
  grid-template-rows: 5rem auto;
  height: 100%;
  box-sizing: border-box;
  overflow: hidden;
  .modal {
    z-index: 1100;
    position: absolute;
  }
  .header-wrapper {
    padding-top: 0.5rem;
  }
  .search-toolbar {
    display: flex;
    flex-direction: row;
    align-items: flex-start;
    margin-left: 0.5rem;
    margin-bottom: 0.5rem;
    height: 1.5rem;
    grid-gap: 0.5rem;
    .filter-toggle {
      background: unset;
      color: var(--color-text-primary);
      font-family: Montserrat;
      font-size: var(--font-size-medium);
      padding: 0.125rem;
      border-radius: var(--border-radius);
      border-style: solid;
      border-width: 1px;
      margin-bottom: 0.25rem;
      border-color: currentColor;
      &:hover {
        cursor: pointer;
      }
    }
    .map-options-toggle {
      color: var(--color-text-primary);
      .main-action {
        color: var(--color-text-primary);
        border-top-right-radius: 0px;
        border-bottom-right-radius: 0px;
      }
      .voyager-popper {
        border-top-right-radius: var(--border-radius);
        border-bottom-right-radius: var(--border-radius);
      }
    }
  }
  .search-area {
    transition: width .25s;
    display: flex;
    flex-direction: row;
    height: 100%;
    border-radius: 5px;
    overflow: hidden;
    .splitpanes {
      overflow: hidden;
    }
    .map-container {
      height: 100%;
      .map {
        height: 100%;
      }
      resize: horizontal;
    }
    .main-pane {
      touch-action: none;
      padding: 0px;
      min-width: 14rem;
      background-color: hsl(204deg 20% 28% / 0%);
      position: relative;
    }
    .splitpanes__splitter {
      transition: opacity .25s;
      opacity: 0.25;
      background-color: var(--color-action-background-primary);
      width: 0.5rem;
      &:hover {
        opacity: 1;
      }
    }
  }
}
.result-tools {
  width: 100%;
  background-color: grey;
  padding-bottom: 0.5rem;
}
.filter-panel {
  width: 20rem;
  min-width: 20rem;
  max-width: 20rem;
  height: 100%;
  position: relative;
  .ps__rail-y {
    z-index: 1;
  }
  .ps__thumb-y {
    background-color: var(--color-action-background-secondary);
  }
}

.filters-scroller {
  height: 100%;
}

.slide-leave-active,
.slide-enter-active {
  transition: .25s;
  .filter-container {
    white-space: nowrap;
  }
}
.slide-enter-from, .slide-leave-to {
  max-width: 0px;
  min-width: 0px;
  .filter-container {
    white-space: nowrap;
  }
}

// Fade Animation
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.25s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

// Overlay Animation
.overlay-slide-enter-active {
  transition: all .25s ease-out;
  opacity: 1;
}
.overlay-slide-leave-active {
  transition: all .25s ease-out;
  // transform: translateX(-100%);
  opacity: 0;
}
.overlay-slide-enter-from, 
.overlay-slide-leave-to {
  transform: translateX(100%);
  opacity: 0;
}

.pane-overlay {
  position: absolute;
  z-index: 10;
  top: 0px;
  width: 100%;
  height: 100%;
  background-color: var(--app-background)
}

.details-overlay {
  width: 98%;
  height: 98%;
  border-radius: var(--border-radius);
  background-color: yellow;
  position: absolute;
  z-index: 20;
  top: 1%;
  left:1%;
}

</style>